import { useState, useEffect, useRef } from 'react';
import FloatingLabelInput from '../ReusableComponents/FloatingLabelInput';
import AuthenticationService from '../../Core/Services/AuthenticationService';
import SettingsService from '../../Core/Services/SettingsService';
import SuccessCheckmark from './SuccessCheckmark';
import LoadingSpinner from '../ReusableComponents/LoadingSpinner';
import EyeIcon from '../Icons/EyeIcon';
import { useTranslation } from 'react-i18next';

// Email validation pattern
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function LoginForm({ setUser, handleNext, setShowLogin }) {
  const { t } = useTranslation();
  const timeoutRef = useRef(null);
  const [formValues, setFormValues] = useState({});
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [gdprChecked, setGdprChecked] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [showTermsTooltip, setShowTermsTooltip] = useState(false);

  // Cleanup timeout on unmount so we don´t create a memory leak.
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleChange = (key, value) => {
    setFormValues(prev => ({ ...prev, [key]: value }));
    // Clear field error when user starts typing
    if (fieldErrors[key]) {
      setFieldErrors(prev => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
    }
  };

  const validateField = (key, value) => {
    const trimmedValue = typeof value === 'string' ? value.trim() : value;

    switch (key) {
      case 'email':
        if (!trimmedValue) return t('email_required') || 'Email is required';
        if (!EMAIL_PATTERN.test(trimmedValue)) {
          return (
            t('invalid_email_format') || 'Please enter a valid email address'
          );
        }
        break;
      case 'password':
        if (!trimmedValue)
          return t('password_required') || 'Password is required';
        break;
      default:
        return null;
    }
    return null;
  };

  const handleFormSubmit = async e => {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    // Validate fields
    const errors = {};
    const requiredFields = ['email', 'password'];

    for (const field of requiredFields) {
      const error = validateField(field, formValues[field]);
      if (error) {
        errors[field] = error;
      }
    }

    // Check for whitespace-only values
    if (formValues.email && !formValues.email.trim()) {
      errors.email = t('field_cannot_be_empty') || 'This field cannot be empty';
    }
    if (formValues.password && !formValues.password.trim()) {
      errors.password =
        t('field_cannot_be_empty') || 'This field cannot be empty';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError(
        t('please_fix_errors_before_submitting') ||
          'Please fix the errors in the form before submitting'
      );
      return;
    }

    if (!gdprChecked || !termsChecked) {
      setError(
        t('accept_terms_and_conditions') ||
          'You must accept the terms and conditions and privacy policy'
      );
      return;
    }

    setSubmitting(true);
    setSuccess(false);

    try {
      const response = await AuthenticationService.login(
        formValues.email?.trim(),
        formValues.password?.trim()
      );

      if (!response || !response.accessToken) {
        throw new Error(
          t('invalid_credentials') ||
            'Invalid email or password. Please try again.'
        );
      }

      AuthenticationService.saveToken(response.accessToken);
      setSuccess(true);

      // Store timeout in ref for cleanup
      timeoutRef.current = setTimeout(async () => {
        try {
          const userInfo = await AuthenticationService.validateToken();
          if (!userInfo) {
            throw new Error(
              t('failed_to_get_user_info') ||
                'Failed to retrieve user information'
            );
          }
          setUser(userInfo);
          if (handleNext) handleNext();
        } catch (err) {
          setSubmitting(false);
          setSuccess(false);
          setError(
            err.message ||
              t('login_failed') ||
              'Login failed. Please try again.'
          );
        }
      }, 1500);
    } catch (error) {
      setSubmitting(false);
      setError(
        error.message ||
          t('login_failed') ||
          'Login failed. Please check your credentials and try again.'
      );
    }
  };

  return (
    <form onSubmit={handleFormSubmit} className="w-full">
      <div
        className="mb-6 border-gray-300 !border !border-solid p-6 flex items-center gap-2"
        style={{ backgroundColor: '#F5F5F5' }}
      >
        <div className="text-base">{t('dont_have_account')}</div>
        <span
          className="underline text-blue-600 cursor-pointer text-base"
          onClick={() => setShowLogin(false)}
        >
          {t('create_account')}
        </span>
      </div>
      <div className="flex flex-col gap-4">
        {error && (
          <div className="bg-red-100 text-red-700 px-4 py-2 rounded mb-2 text-center animate-pulse">
            {error}
          </div>
        )}
        <div>
          <FloatingLabelInput
            type="email"
            label="Email"
            value={formValues.email || ''}
            onChange={e => handleChange('email', e.target.value)}
            disabled={success}
            error={fieldErrors.email}
          />
          {fieldErrors.email && (
            <p className="text-red-600 text-xs mt-1">{fieldErrors.email}</p>
          )}
        </div>
        <div>
          <FloatingLabelInput
            type={showPassword ? 'text' : 'password'}
            label={t('password')}
            value={formValues.password || ''}
            onChange={e => handleChange('password', e.target.value)}
            disabled={success}
            error={fieldErrors.password}
            suffix={
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowPassword(v => !v)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                }}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <EyeIcon open={showPassword} />
              </button>
            }
          />
          {fieldErrors.password && (
            <p className="text-red-600 text-xs mt-1">{fieldErrors.password}</p>
          )}
        </div>
        <div className="mb-2">
          <label className="flex items-start gap-2 text-gray-700 text-sm">
            <input
              type="checkbox"
              checked={gdprChecked && termsChecked}
              onChange={() => {
                setGdprChecked(v => !v);
                setTermsChecked(v => !v);
              }}
              className="form-checkbox h-4 w-4 text-blue-600 mt-1 shrink-0"
            />
            <span className="flex flex-wrap items-center gap-1">
              {t('i_accept')}{' '}
              <a
                href={SettingsService.getPrivacyPolicyUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-primaryblue"
                style={{ color: '#444' }}
              >
                {t('privacy_policy')}
              </a>
              {t('and') || 'og'}
              <a
                href={SettingsService.getTermsAndConditionsUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-primaryblue"
                style={{ color: '#444' }}
              >
                {t('terms_and_conditions')}
              </a>
              <div className="relative inline-block">
                <span
                  style={{ color: '#444' }}
                  className="underline cursor-pointer"
                  onClick={() => setShowTermsTooltip(!showTermsTooltip)}
                >
                  {t('online_booking_terms_title')}
                </span>
                {showTermsTooltip && (
                  <div
                    className="absolute bottom-full left-0 mb-2 w-[90vw] max-w-[400px] max-h-[60vh] overflow-y-auto p-4 bg-white border border-gray-300 rounded shadow-lg z-50 text-xs text-left"
                    style={{ color: '#444' }}
                  >
                    <p className="font-bold mb-2">
                      {t('online_payment_terms_heading')}
                    </p>
                    <p className="mb-2">{t('online_payment_terms_intro')}</p>
                    <p className="font-semibold mb-1">
                      {t('online_payment_terms_booking_heading')}
                    </p>
                    <p className="mb-2">{t('online_payment_terms_booking')}</p>
                    <p className="font-semibold mb-1">
                      {t('online_payment_terms_subscription_heading')}
                    </p>
                    <p className="mb-2">
                      {t(
                        SettingsService.getRateTranslationKey(
                          'online_payment_terms_subscription'
                        )
                      )}
                    </p>
                    <p className="font-semibold mb-1">
                      {t('online_payment_terms_billing_heading')}
                    </p>
                    <p className="mb-2">
                      {t(
                        SettingsService.getRateTranslationKey(
                          'online_payment_terms_billing'
                        )
                      )}
                    </p>
                    <p className="font-semibold mb-1">
                      {t('online_payment_terms_update_heading')}
                    </p>
                    <p className="mb-2">{t('online_payment_terms_update')}</p>
                    <p className="font-semibold mb-1">
                      {t('online_payment_terms_failure_heading')}
                    </p>
                    <p className="mb-2">{t('online_payment_terms_failure')}</p>
                    <p className="font-semibold mb-1">
                      {t('online_payment_terms_cancellation_heading')}
                    </p>
                    <p className="mb-0">
                      {t('online_payment_terms_cancellation')}
                    </p>
                  </div>
                )}
              </div>
            </span>
          </label>
        </div>
      </div>
      <div className="pt-4">
        {success ? (
          <SuccessCheckmark />
        ) : submitting ? (
          <div className="flex justify-center py-2">
            <LoadingSpinner />
          </div>
        ) : (
          <button
            type="submit"
            fullWidth
            id="tcrs-button"
            className="tcrs-button text-white rounded-none shadow transition-colors font-semibold text-base py-2 bg-primaryblue hover:bg-primaryblue/90 focus:bg-primaryblue/90 border-none"
          >
            <div className="text-white">{t('log_in_and_confirm_booking')}</div>
          </button>
        )}
      </div>
    </form>
  );
}
